import { useState } from "react";
import { ActivityIndicator, Pressable, TextInput, Platform, View, Text } from "react-native";
import KeyboardAwareScroll from "@/components/KeyboardAwareScroll";
import styled from "styled-components/native";
import { collection, addDoc, Timestamp } from "firebase/firestore";
import { db } from "@/lib/firebase";
import { useAuth } from "@/contexts/AuthContext";
import { useClub } from "@/contexts/ClubContext";
import { useLocalSearchParams } from "expo-router";
import { colors, radius, font } from "@/lib/theme";
import ContentWidth from "@/components/ContentWidth";

const Section = styled.View`
  padding: 20px;
`;

const Title = styled.Text`
  font-size: 22px;
  font-weight: ${font.extrabold};
  color: ${colors.ink};
  margin-bottom: 8px;
`;

const Subtitle = styled.Text`
  font-size: 14px;
  color: ${colors.inkSecondary};
  line-height: 20px;
  margin-bottom: 24px;
`;

const Label = styled.Text`
  font-size: 11px;
  font-weight: ${font.bold};
  color: ${colors.inkMuted};
  text-transform: uppercase;
  letter-spacing: 1px;
  margin-bottom: 8px;
`;

const MessageInput = styled(TextInput)`
  background-color: ${colors.white};
  border-width: 1px;
  border-color: ${colors.line};
  border-radius: ${radius.card}px;
  padding: 16px;
  font-size: 15px;
  color: ${colors.ink};
  min-height: 120px;
  text-align-vertical: top;
`;

const CharCount = styled.Text<{ over?: boolean }>`
  font-size: 12px;
  color: ${(p: { over?: boolean }) => (p.over ? colors.danger : colors.inkMuted)};
  text-align: right;
  margin-top: 4px;
`;

const SubmitBtn = styled.View<{ disabled?: boolean }>`
  background-color: ${(p: { disabled?: boolean }) => (p.disabled ? colors.inkMuted : colors.brand)};
  border-radius: ${radius.card}px;
  padding: 16px;
  align-items: center;
  margin-top: 16px;
`;

const SubmitText = styled.Text`
  color: #fff;
  font-size: 16px;
  font-weight: ${font.bold};
`;

const FaqCard = styled.View`
  background-color: ${colors.white};
  border-radius: ${radius.lg}px;
  border-width: 1px;
  border-color: ${colors.line};
  margin-bottom: 8px;
  overflow: hidden;
`;

const FaqQuestion = styled.Text`
  font-size: 15px;
  font-weight: ${font.bold};
  color: ${colors.ink};
  flex: 1;
`;

const FaqAnswer = styled.Text`
  font-size: 14px;
  color: ${colors.inkSecondary};
  line-height: 20px;
  padding: 0px 16px 16px;
`;

const SectionLabel = styled.Text`
  font-size: 12px;
  font-weight: ${font.extrabold};
  color: ${colors.inkMuted};
  text-transform: uppercase;
  letter-spacing: 1px;
  margin-bottom: 12px;
`;

const SuccessContainer = styled.View`
  flex: 1;
  justify-content: center;
  align-items: center;
  padding: 24px;
`;

const FAQS = [
  {
    q: "How do I set my availability for a match?",
    a: "Go to Matches, tap the match, and select Available, Unavailable, or Maybe. You can also add a note.",
  },
  {
    q: "How do I pay my match fees?",
    a: "Go to My Fees from the Club tab. If your club has payments enabled, you can pay directly through the app.",
  },
  {
    q: "How do I change my profile details?",
    a: "Go to Club > My Profile. You can update your name, phone number, emergency contact, and medical conditions.",
  },
  {
    q: "How do invites work?",
    a: "Your club admin sends an invite to your email. Click the link to join. If the link expires, ask them to resend it.",
  },
  {
    q: "Can I be in multiple clubs?",
    a: "Yes! If you're invited to another club, you'll be added to it. Use the Switch Club button on the Club tab to switch between them.",
  },
  {
    q: "How do I get directions to a match?",
    a: "If the fixture has a location set, you'll see a Directions button on the match card that opens Google Maps.",
  },
  {
    q: "How do I delete my account?",
    a: "Go to the Club tab and scroll to the bottom. Tap 'Delete account' and confirm. This permanently removes your account from all clubs. You must settle any unpaid fees first.",
  },
];

const MAX_CHARS = 2000;

export default function HelpScreen() {
  const { user } = useAuth();
  const { membership } = useClub();
  const params = useLocalSearchParams<{ contactOnly?: string }>();
  const contactOnly = params.contactOnly === "true";
  const [message, setMessage] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [expandedFaq, setExpandedFaq] = useState<number | null>(null);

  const canSubmit = message.trim().length > 0 && message.length <= MAX_CHARS && !submitting;

  async function handleSubmit() {
    if (!canSubmit) return;
    setSubmitting(true);
    try {
      await addDoc(collection(db, "helpRequests"), {
        message: message.trim(),
        email: user?.email || "unknown",
        userId: user?.uid || null,
        clubId: membership?.clubId || null,
        clubName: membership?.clubName || null,
        platform: Platform.OS,
        createdAt: Timestamp.now(),
        status: "new",
      });
      setSubmitted(true);
    } catch (err) {
      console.error("Help submit error:", err);
    } finally {
      setSubmitting(false);
    }
  }

  if (submitted) {
    return (
      <SuccessContainer>
        <Text style={{ fontSize: 48, marginBottom: 16 }}>🏏</Text>
        <Text style={{ fontSize: 22, fontWeight: "800", color: colors.ink, marginBottom: 8 }}>Message sent</Text>
        <Text style={{ fontSize: 15, color: colors.inkSecondary, textAlign: "center", lineHeight: 22 }}>
          We'll get back to you at {user?.email || "your email"} as soon as we can.
        </Text>
      </SuccessContainer>
    );
  }

  return (
    <KeyboardAwareScroll style={{ flex: 1, backgroundColor: colors.bg }}>
      <ContentWidth>
        {!contactOnly && (
          <Section>
            <SectionLabel>Frequently Asked Questions</SectionLabel>
            {FAQS.map((faq, i) => (
              <FaqCard key={i}>
                <Pressable
                  onPress={() => setExpandedFaq(expandedFaq === i ? null : i)}
                  style={{ flexDirection: "row", alignItems: "center", padding: 16 }}
                >
                  <FaqQuestion>{faq.q}</FaqQuestion>
                  <Text style={{ fontSize: 18, color: colors.inkMuted, marginLeft: 8 }}>
                    {expandedFaq === i ? "▾" : "›"}
                  </Text>
                </Pressable>
                {expandedFaq === i && <FaqAnswer>{faq.a}</FaqAnswer>}
              </FaqCard>
            ))}
          </Section>
        )}

        <Section>
          <SectionLabel>Contact Us</SectionLabel>
          <Label>Your message</Label>
          <MessageInput
            placeholder="Describe your issue or question..."
            placeholderTextColor={colors.inkMuted}
            multiline
            maxLength={MAX_CHARS + 100}
            value={message}
            onChangeText={setMessage}
            textAlignVertical="top"
          />
          <CharCount over={message.length > MAX_CHARS}>
            {message.length}/{MAX_CHARS}
          </CharCount>

          <Pressable onPress={handleSubmit} disabled={!canSubmit} style={({ pressed }) => ({ opacity: pressed ? 0.9 : 1 })}>
            <SubmitBtn disabled={!canSubmit}>
              {submitting ? <ActivityIndicator color="#fff" size="small" /> : <SubmitText>Send message</SubmitText>}
            </SubmitBtn>
          </Pressable>
        </Section>

        <View style={{ height: 40 }} />
      </ContentWidth>
    </KeyboardAwareScroll>
  );
}
